import { useMemo } from 'react';
import { OptionKey, useQuery } from '@common-fe/common-fe';

import { api } from '@/api';
import { API_URL } from '@/dtos';

import { IAccountType, TotalClaimPayload } from '../../EmployerDashboard.types';

interface Params {
  disabled?: boolean;
  organizationId?: string;
  planYearId?: OptionKey | null;
}
const useGetPlanListQuery = ({ disabled, organizationId, planYearId }: Params) => {
  const { isLoading, data, isError } = useQuery(
    [API_URL.METRICS_ACCOUNT_USAGE_TOTAL_CLAIM_SPEND, organizationId, planYearId],
    () =>
      api.get(API_URL.METRICS_ACCOUNT_USAGE_TOTAL_CLAIM_SPEND, {
        params: {
          plan_year_id: planYearId,
        },
      }),
    {
      enabled: !disabled,
      retry: 0,
    }
  );
  const maxValue = useMemo(() => {
    const currentData = data?.data;
    if (!currentData || !currentData.length) {
      return 0;
    }
    return Math.max(...currentData.map((item) => Math.abs(item.total_claim_spend || 0)));
  }, [data]);

  const formattedData = useMemo<TotalClaimPayload[] | null>(() => {
    const currentData = data?.data;
    if (!currentData || !currentData.length) {
      return null;
    }

    const list = currentData.map(
      (item): TotalClaimPayload => ({
        id: `${item.plan_id || ''}`,
        planName: item.plan_name || '',
        totalSpend: Math.abs(item.total_claim_spend || 0),
        percentage: item.total_claim_spend ? (Math.abs(item.total_claim_spend || 0) / maxValue) * 100 : 0,
        accountType: item.account_type as unknown as IAccountType,
      })
    );
    const sortedList = list.sort((a, b) => {
      if (b.totalSpend !== a.totalSpend) {
        return b.totalSpend - a.totalSpend;
      }
      return a.planName.localeCompare(b.planName);
    });
    return sortedList;
  }, [data?.data, maxValue]);

  return {
    isLoading,
    isError,
    data: formattedData,
  };
};

export default useGetPlanListQuery;
